import React, { useEffect, useMemo, useState } from "react";
import { t } from "../i18n";
import Sidebar from "../components/Sidebar.jsx";
import MediaSelector from "../components/MediaSelector.jsx";
import { fetchBillingStatus, fetchCreditEstimate } from "../services/billing.js";
import "./NanoStudio.css";

const MAX_IMAGES = 14;
const DEFAULT_CONTEXT = {
  child: { level: "CE2", speed: "slow", repeat: 1, help: "hard_words" }
};

export default function InteriorStudio() {
  const [selectedMediaEntries, setSelectedMediaEntries] = useState([]);
  const [promptText, setPromptText] = useState("");
  const [sending, setSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [billingStatus, setBillingStatus] = useState(null);
  const [creditEstimate, setCreditEstimate] = useState(1);
  const [billingError, setBillingError] = useState("");

  useEffect(() => {
    async function consumePickerSelection() {
      const params = new URLSearchParams(window.location.search);
      if (params.get("media_picker_done") !== "1") return;
      if ((params.get("target") || "") !== "interior") return;
      const clearSelection = async () => {
        try {
          await fetch("/api/fast/media/selection", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ image_paths: [], target: "interior" })
          });
        } catch {
          // ignore
        }
      };
      let handled = false;
      try {
        const raw = window.sessionStorage.getItem("contentos_media_picker_payload");
        if (raw) {
          const payload = JSON.parse(raw);
          if (payload?.target === "interior" && Array.isArray(payload.images)) {
            const images = payload.images;
            const versions = Array.isArray(payload.versions) ? payload.versions : [];
            const entries = versions.length
              ? versions.map((item) => ({
                url: item.url,
                thumbnail_url: item.thumbnailUrl || "",
                mediaItemId: item.mediaItemId || "",
                mediaVersionId: item.mediaVersionId || "",
                assetId: item.assetId || ""
              }))
              : images.map((url) => ({ url }));
            setSelectedMediaEntries(entries.slice(0, MAX_IMAGES));
            window.sessionStorage.removeItem("contentos_media_picker_payload");
            handled = true;
          }
        }
      } catch {
        // ignore
      }
      try {
        if (!handled) {
          const res = await fetch("/api/fast/media/selection");
          if (res.ok) {
            const data = await res.json();
            const selection = data.selection || {};
            const images = Array.isArray(selection.images) ? selection.images : [];
            if (images.length) {
              setSelectedMediaEntries(images.slice(0, MAX_IMAGES).map((url) => ({ url })));
            }
          }
        }
      } finally {
        await clearSelection();
        params.delete("media_picker_done");
        params.delete("target");
        const base = window.location.pathname;
        const query = params.toString();
        window.history.replaceState({}, "", query ? `${base}?${query}` : base);
      }
    }
    consumePickerSelection();
  }, []);

  useEffect(() => {
    let isMounted = true;
    fetchBillingStatus()
      .then((data) => {
        if (!isMounted) return;
        setBillingStatus(data);
      })
      .catch((err) => {
        if (!isMounted) return;
        setBillingError(err.message || "Erreur billing.");
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    fetchCreditEstimate({
      gen_type: "ocr",
      model_id: "pixtral"
    })
      .then((data) => {
        if (!isMounted) return;
        setCreditEstimate(Number(data.credits || 1));
      })
      .catch(() => {
        if (!isMounted) return;
        setCreditEstimate(1);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const handleSelectionChange = (entries) => {
    const safeEntries = Array.isArray(entries) ? entries.slice(0, MAX_IMAGES) : [];
    setSelectedMediaEntries(safeEntries);
    setErrorMsg("");
    setSuccessMsg("");
  };

  const handlePromptChange = (event) => {
    setPromptText(event.target.value);
    setErrorMsg("");
    setSuccessMsg("");
  };

  const imageUrls = useMemo(
    () => selectedMediaEntries.map((entry) => entry?.url).filter(Boolean),
    [selectedMediaEntries]
  );
  const creditsRemaining = billingStatus?.credits_remaining;
  const creditsNeeded = Number.isFinite(creditEstimate) ? creditEstimate : 1;
  const hasCredits = typeof creditsRemaining === "number" ? creditsRemaining >= creditsNeeded : true;
  const missingCredits =
    typeof creditsRemaining === "number" ? Math.max(creditsNeeded - creditsRemaining, 0) : 0;

  async function sendRequest() {
    if (sending) return;
    const trimmedPrompt = promptText.trim();
    if (!imageUrls.length) {
      setErrorMsg(t("interior.send.missingImages"));
      return;
    }
    if (!trimmedPrompt) {
      setErrorMsg(t("interior.send.missingPrompt"));
      return;
    }
    setSending(true);
    setErrorMsg("");
    setSuccessMsg("");
    try {
      const payload = {
        context: DEFAULT_CONTEXT,
        prompt: trimmedPrompt,
        images: imageUrls
      };
      const res = await fetch("/api/fast/interior/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || t("interior.send.error"));
      setSuccessMsg(t("interior.send.success"));
    } catch (err) {
      setErrorMsg(err.message || t("interior.send.error"));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="app-shell">
      <Sidebar active="interior" />
      <div className="app-main">
        <div className="nano-page">
          <header className="nano-header">
            <div>
              <p className="nano-kicker">{t("interior.kicker")}</p>
              <h1 className="nano-title page-title-accent">{t("interior.title")}</h1>
              <p className="nano-subtitle">{t("interior.subtitle")}</p>
            </div>
          </header>

          {errorMsg && <div className="nano-alert error">{errorMsg}</div>}
          {successMsg && <div className="nano-alert success">{successMsg}</div>}

          <div className="nano-layout">
            <section className="nano-panel nano-panel-wide">
              <h2>{t("interior.panel.result")}</h2>
              {!imageUrls.length && <p className="nano-muted">{t("interior.image.empty")}</p>}
              {imageUrls.length > 0 && (
                <div className="interior-gallery">
                  {selectedMediaEntries.map((entry) => (
                    <div key={entry.url} className="interior-thumb">
                      <img src={entry.thumbnail_url || entry.url} alt="interior selection" />
                    </div>
                  ))}
                </div>
              )}
              <label className="interior-prompt" htmlFor="interior-prompt">
                {t("interior.prompt.label")}
                <textarea
                  id="interior-prompt"
                  value={promptText}
                  onChange={handlePromptChange}
                  placeholder={t("interior.prompt.placeholder")}
                />
              </label>
              <div className="interior-actions">
                <button
                  type="button"
                  className="nano-primary"
                  onClick={sendRequest}
                  disabled={sending || !imageUrls.length || !promptText.trim() || !hasCredits}
                >
                  {sending ? t("interior.send.loading") : t("interior.send")}
                  <span className="credit-badge">{creditsNeeded}</span>
                </button>
              </div>
              {billingError && <div className="nano-alert error">{billingError}</div>}
              {!hasCredits && missingCredits > 0 && (
                <button
                  type="button"
                  className="billing-upgrade-link"
                  onClick={() => (window.location.href = "/pricing")}
                >
                  {missingCredits} / {creditsNeeded} credits manquants rechargez !
                </button>
              )}
            </section>

            <MediaSelector
              title={t("interior.panel.input")}
              selected={selectedMediaEntries}
              onChange={handleSelectionChange}
              maxCount={MAX_IMAGES}
              minCount={1}
              allowUpload
              allowLibrary
              pickerTarget="interior"
              pickerReturn="/interior"
              pickerMulti
              gridLimit={14}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
